import { FileCode2 } from 'lucide-react';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

import { toScreenOptions } from './screen-paths';

export interface ScreenSelectProps {
  /** Every `.html` path the archive contained, in the archive's order. */
  readonly screenPaths: readonly string[];
  /** The entry among {@link screenPaths} on display, or `null` if unknown. */
  readonly value: string | null;
  readonly onChange: (screenPath: string) => void;
  /** Disabled while the agent cannot be reached — navigation would go nowhere. */
  readonly disabled?: boolean;
}

/**
 * Direct navigation to any screen in the archive (spec R3.3).
 *
 * The value is the raw `screenPaths` entry, not a normalised one, so the option
 * the agent's reported location resolves to is the option the trigger shows.
 * `screen-paths.ts` owns that resolution — the two sides spell a screen
 * differently and reconciling them here would put URL parsing in a component.
 *
 * A prototype with a single screen still renders the control, disabled: hiding
 * it would make the toolbar's shape depend on the archive, and a reviewer would
 * be left wondering where the dropdown went.
 */
export const ScreenSelect = ({
  screenPaths,
  value,
  onChange,
  disabled = false,
}: ScreenSelectProps) => {
  const options = toScreenOptions(screenPaths);
  const isEmpty = options.length === 0;

  return (
    <Select
      /*
       * Omitted rather than passed as `undefined` (`exactOptionalPropertyTypes`
       * is on), which also leaves Radix genuinely uncontrolled until the agent
       * has said where the iframe is — so the placeholder shows instead of an
       * empty trigger.
       */
      {...(value === null ? {} : { value })}
      onValueChange={onChange}
      disabled={disabled || isEmpty || options.length === 1}
    >
      <SelectTrigger
        className="h-8 w-auto min-w-[9rem] max-w-[16rem] gap-2 border-viewer-chrome-border bg-transparent px-2.5 text-xs font-normal text-viewer-chrome-foreground shadow-none hover:bg-accent hover:text-accent-foreground focus:ring-1 disabled:opacity-60"
        aria-label="Screen"
      >
        <FileCode2 aria-hidden="true" className="size-3.5 shrink-0 opacity-70" />
        <SelectValue
          placeholder={isEmpty ? 'No screens' : 'Somewhere in the prototype'}
        />
      </SelectTrigger>
      <SelectContent align="end" className="max-h-80">
        {options.map((option) => (
          <SelectItem key={option.value} value={option.value} className="text-xs">
            <span className="flex items-baseline gap-1">
              {option.folder === '' ? null : (
                <span className="text-muted-foreground">{option.folder}</span>
              )}
              <span>{option.label}</span>
            </span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};
